/**
 * Profile business logic operations - separated from React state
 */

import { 
  BusinessProfile,
  loadProfilesFromSupabase,
  saveProfileToSupabase,
  updateProfileInSupabase,
  deleteProfileFromSupabase,
  isUserAuthenticated,
  getCurrentActiveProfileId,
  setCurrentActiveProfileId
} from '@/lib/supabase-profiles-operations';
import { EnhancedSupabaseDataManager } from '@/lib/enhanced-supabase-data-management';
import type { Profile } from './types';

// Re-export storage utilities
export { getCurrentActiveProfileId, setCurrentActiveProfileId, isUserAuthenticated };

/**
 * Convert BusinessProfile (Supabase) to Profile (app)
 */
export const convertBusinessProfileToProfile = (bp: BusinessProfile): Profile => ({
  id: bp.id,
  name: bp.name,
  email: '', // Email não é mais usado nos perfis
  type: bp.type,
  color: bp.color,
  isActive: bp.is_active,
  timezone: bp.timezone,
  createdAt: bp.created_at,
  cardExpenseDisplayDate: bp.card_expense_display_date || 'due_date'
});

/**
 * Convert Profile (app) to BusinessProfile data (Supabase)
 */
export const convertProfileToBusinessProfileData = (
  profile: Omit<Profile, 'id' | 'createdAt'>
): Omit<BusinessProfile, 'id' | 'user_id' | 'created_at' | 'updated_at'> => ({
  name: profile.name,
  type: profile.type,
  color: profile.color,
  is_active: profile.isActive,
  timezone: profile.timezone
});

/**
 * Convert partial Profile updates to BusinessProfile format
 */
export const convertProfileUpdatesToBusinessProfile = (
  updates: Partial<Profile>
): Partial<BusinessProfile> => {
  const businessProfileUpdates: Partial<BusinessProfile> = {};
  if (updates.name !== undefined) businessProfileUpdates.name = updates.name;
  if (updates.type !== undefined) businessProfileUpdates.type = updates.type;
  if (updates.color !== undefined) businessProfileUpdates.color = updates.color;
  if (updates.isActive !== undefined) businessProfileUpdates.is_active = updates.isActive;
  if (updates.timezone !== undefined) businessProfileUpdates.timezone = updates.timezone;
  if (updates.cardExpenseDisplayDate !== undefined) businessProfileUpdates.card_expense_display_date = updates.cardExpenseDisplayDate;
  return businessProfileUpdates;
};

/**
 * Load profiles from Supabase and convert to app format
 */
export const loadProfiles = async (): Promise<Profile[]> => {
  console.log('[Profiles] Loading profiles from Supabase...');
  const businessProfiles = await loadProfilesFromSupabase();
  return businessProfiles.map(convertBusinessProfileToProfile);
};

/**
 * Determine which profile should be active
 */
export const determineActiveProfile = (profiles: Profile[]): Profile | null => {
  if (profiles.length === 0) return null;
  
  const savedActiveProfileId = getCurrentActiveProfileId();
  
  if (savedActiveProfileId) {
    const savedProfile = profiles.find(p => p.id === savedActiveProfileId);
    if (savedProfile) return savedProfile;
  }
  
  // Fallback: find first active or first profile
  const activeProfile = profiles.find(p => p.isActive) || profiles[0];
  if (activeProfile) {
    setCurrentActiveProfileId(activeProfile.id);
  }
  
  return activeProfile;
};

/**
 * Create a new profile in Supabase
 */
export const createProfile = async (
  profileData: Omit<Profile, 'id' | 'createdAt'>
): Promise<Profile> => {
  if (!await isUserAuthenticated()) {
    throw new Error('Usuário não autenticado');
  }
  
  console.log('[Profiles] Creating new profile:', profileData.name);
  const businessProfileData = convertProfileToBusinessProfileData(profileData);
  const savedBusinessProfile = await saveProfileToSupabase(businessProfileData);
  return convertBusinessProfileToProfile(savedBusinessProfile);
};

/**
 * Update an existing profile in Supabase
 */
export const updateProfileOperation = async (
  id: string,
  updates: Partial<Profile>
): Promise<void> => {
  if (!await isUserAuthenticated()) {
    throw new Error('Usuário não autenticado');
  }
  
  console.log('[Profiles] Updating profile:', id);
  const businessProfileUpdates = convertProfileUpdatesToBusinessProfile(updates);
  await updateProfileInSupabase(id, businessProfileUpdates);
};

/**
 * Delete a profile from Supabase
 */
export const deleteProfileOperation = async (id: string): Promise<void> => {
  if (!await isUserAuthenticated()) {
    throw new Error('Usuário não autenticado');
  }
  
  console.log('[Profiles] Deleting profile:', id);
  await deleteProfileFromSupabase(id);
};

/**
 * Clear all data associated with a profile
 */
export const clearProfileDataOperation = async (profileId: string): Promise<void> => {
  console.log(`[Profiles] Clearing data for profile: ${profileId}`);
  
  const isAuth = await isUserAuthenticated();
  
  if (isAuth) {
    console.log(`[Profiles] User authenticated - clearing Supabase data for profile ${profileId}...`);
    const dataManager = EnhancedSupabaseDataManager.getInstance();
    await dataManager.clearAllUserDataFromSupabase(profileId);
    console.log('[Profiles] Supabase data cleared successfully');
  } else {
    console.log('[Profiles] User not authenticated - local cleanup only');
  }
  
  // Clear legacy local data for this profile (migration cleanup only)
  // All data is now stored in Supabase, but we clean up any residual localStorage
  const legacyKeys = [
    `transactions_${profileId}`,
    `categories_${profileId}`,
    `accounts_${profileId}`,
    `cards_${profileId}`,
    `budgets_${profileId}`,
    `goals_${profileId}`,
    `futureReleases_${profileId}`,
    `installmentGroups_${profileId}`,
    `debts_${profileId}`,
    `debt_installments_${profileId}`,
    `creditCardBills_${profileId}`,
  ];

  legacyKeys.forEach(key => {
    localStorage.removeItem(key);
  });

  console.log(`[Profiles] Data cleared for profile ${profileId}`);
};
